import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { Lightbulb, Globe, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";


gsap.registerPlugin(ScrollTrigger);

const text = [
  "CV Gerbang Ilmu Nusantara (GENIUS) adalah perusahaan yang didirikan dengan visi untuk menjadi katalisator kemajuan di berbagai bidang ilmu pengetahuan, teknologi, dan pengembangan masyarakat.",
  "Sebagai entitas yang dinamis dan inovatif, GENIUS hadir untuk memberikan solusi yang terintegrasi melalui layanan yang beragam dan berfokus pada hasil yang berdampak positif",
];

const icons = [
  { icon: <Lightbulb className="w-10 h-10 text-darkRed" />, text: "Inovasi" },
  { icon: <Globe className="w-10 h-10 text-darkRed" />, text: "Teknologi" },
  { icon: <Users className="w-10 h-10 text-darkRed" />, text: "Masyarakat" },
];

const wordsRight = [
  `G - Growth (pertumbuhan)
  Kami berkomitmen untuk mendukung pertumbuhan, baik dalam pengembangan individu, organisasi, maupun masyarakat. 
  GENIUS percaya bahwa pertumbuhan adalah fondasi menuju kesuksesan yang berkelanjutan.`,

  `E - Execellence (keunggulan)
  Dalam setiap layanan yang kami tawarkan, GENIUS mengutamakan keunggulan. 
  Kami berusaha memberikan yang terbaik melalui inovasi, dedikasi, dan hasil yang berkualitas tinggi.`,

  `N - Nurture (pembinaan)
  Kami mendorong pembinaan talenta, ide, dan kolaborasi untuk menciptakan solusi yang bermanfaat. 
  GENIUS berperan sebagai mitra bagi pertumbuhan ilmu pengetahuan dan kemampuan masyarakat.`,

  `I - Integrity (integritas)
  Kepercayaan adalah landasan utama kami. 
  GENIUS menjunjung tinggi nilai kejujuran, transparansi, dan etika dalam setiap aspek operasional perusahaan.`,

  `U - Unity (kesatuan)
  Kami percaya bahwa kolaborasi dan kesatuan adalah kunci untuk mencapai hasil terbaik. 
  GENIUS mendorong kerja sama lintas sektor untuk menciptakan dampak yang lebih besar.`,

  `S - Sustainibility (keberlanjutan)
  GENIUS berkomitmen untuk menciptakan solusi yang berkelanjutan, baik dalam aspek sosial, ekonomi, maupun lingkungan, demi masa depan yang lebih baik.`
];

const About = () => {

  const containerRef = useRef<HTMLDivElement>(null);
  const containerTentangRef = useRef<HTMLDivElement>(null);
  const horizontalRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const scrollWordsRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    const mm = gsap.matchMedia();

    // Efek kata scroll hover
    const words = containerTentangRef.current?.querySelectorAll(".word");
    if (words && words.length > 0) {
      gsap.set(words, { opacity: 0.3, color: "#CCCCCC" });
  
      gsap.to(words, {
        opacity: 1,
        color: "#CCCCCC",
        stagger: 0.08,
        scrollTrigger: {
          trigger: containerTentangRef.current,
          start: "top 70%",
          end: "bottom 60%",
          scrub: true,
          markers: false,
        },
      });
    }
        
    // Horizontal Scroll
    const horizontalElem = horizontalRef.current;
    const panel = panelRef.current;
    if (horizontalElem && panel) {
      mm.add(
        {
          isDesktop: "(min-width: 768px)",
          isMobile: "(max-width: 767px)",
        },
        (context) => {
          const isDesktop = context.conditions?.isDesktop;
          const offset = isDesktop ? 0 : 40;

          gsap.to(panel, {
            x: () => -(panel.scrollWidth - window.innerWidth + offset),
            ease: "none",
            scrollTrigger: {
              trigger: horizontalElem,
              start: isDesktop ? "top 30%" : "top 25%",
              end: () => `+=${panel.scrollWidth}`,
              scrub: true,
              pin: true,
              anticipatePin: 1,
              invalidateOnRefresh: true,
            },
          });

          return () => {
            // Bersihkan trigger horizontal
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
          };
        }
      );
    }

    // Clean up all ScrollTriggers on unmount
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: `+=${wordsRight.length * 100}vh`,
          scrub: true,
          pin: true,
        },
      });

      scrollWordsRef.current.forEach((el, i) => {
        // Munculkan elemen
        tl.to(el, { opacity: 1, duration: 0.3 }, i);
        // Sembunyikan sebelum masuk elemen berikutnya
        if (i !== scrollWordsRef.current.length - 1) {
          tl.to(el, { opacity: 0, duration: 0.3 }, i + 0.7);
        }
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const [showShine, setShowShine] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowShine(true);
      setTimeout(() => setShowShine(false), 800); // Lama shine muncul
    }, 2000); // Muncul setiap 2.5 detik

    return () => clearInterval(interval);
  }, []);
    
  // Navigasi dengan animasi
  const handleNavigate = () => {
    gsap.to("#home", {
      opacity: 0,
      y: -100,
      duration: 0.8,
      ease: "power3.inOut",
      onComplete: () => {
        navigate("/home");
        setTimeout(() => ScrollTrigger.refresh(), 500); // kasih delay sedikit agar DOM benar-benar berubah
      },
    });
  };
  
  return (
      <section
        className="relative min-h-screen bg-white/50 py-24 px-6 md:px-12 lg:px-32 text-gray-500 overflow-x-hidden"
        id="about"
      >
        {/* Background Decorations */}
        <div className="absolute -top-40 -left-48 w-[700px] h-[700px] bg-primary dark:bg-white rounded-full filter blur-[10px] opacity-70 z-0"></div>
        <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] bg-white rounded-full filter blur-[5px] opacity-60 z-0"></div>
        <div className="absolute top-[30%] left-[10%] w-[300px] h-[300px] bg-darkRed rounded-full opacity-90 animate-pulse blur-xl z-0"></div>
        <div className="absolute bottom-[20%] right-[15%] w-40 h-40 bg-primary rounded-full opacity-40 animate-ping blur-xl z-0"></div>

        {/* Gradient Overlay */}
        <div className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none opacity-90 
          bg-hero-red transition-colors duration-300
          dark:bg-dark-surface transition-colors duration-300
          ">
        </div>
        
        {/* Heading Section */}
        <div className="relative z-10 text-center mb-20">
          <motion.h2
            className="text-4xl md:text-5xl font-extrabold text-backgroundDark"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Tentang Kami
          </motion.h2>
          <motion.div
            className="w-24 h-1 bg-white/70 mx-auto mt-4 rounded-full"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
          <p className="mt-4 text-lg text-gray-400 max-w-2xl mx-auto">
            Menjadi katalisator perubahan melalui inovasi, teknologi, dan pemberdayaan masyarakat.
          </p>
        </div>

        {/* Animated Text */}
        <div
          ref={containerTentangRef}
          className="relative z-10 max-w-6xl mx-auto text-[28px] md:text-[50px] leading-snug font-bold space-y-12 text-justify"
        >
          {text.map((sentence, i) => (
            <p key={i} className="transition-all duration-900 ease-in-out">
              {sentence.split(" ").map((word, j) => (
                <span
                  key={j}
                  className="word inline-block mr-2 transform transition-transform hover:scale-110 hover:text-darkGreen text-left"
                >
                  {word}
                </span>
              ))}
            </p>
          ))}
        </div>

        {/* Horizontal Scroll Section */}
        <div ref={horizontalRef} className="relative z-10 mt-32 h-screen overflow-visible">
          <div ref={panelRef} className="flex space-x-6 md:space-x-16 w-max h-full px-6 md:px-10">
            
            {/* Tentang */}
              <div className="w-[85vw] md:w-[80vw] h-[80vh] flex flex-col justify-center 
              bg-gradient-to-br from-[#DE3433]/80 via-[#A9A9A9]/10 to-[#909194]/80 
              backdrop-blur-md rounded-3xl p-6 md:p-10 text-white 
              shadow-[10px_-10px_20px_1px_rgba(0,_0,_0,_0.1)] 
              border border-white/10 ring-1 ring-white/5 transition-all duration-300 
              hover:scale-[1.02] hover:shadow-[12px_-12px_24px_2px_rgba(0,_0,_0,_0.15)] ease-in-out">
                          
              <h3 className="text-3xl md:text-hero-l lg:text-hero-xxl font-extrabold mb-4 md:mb-6 tracking-wider md:tracking-wider lg:tracking-widest">Visi Misi Kami</h3>
            </div>
            
            {/* VISI */}
            <div className="w-[85vw] md:w-[80vw] h-[80vh] flex flex-col justify-center 
                bg-gradient-to-br from-[#DE3433]/80 via-[#A9A9A9]/10 to-[#909194]/80 
                backdrop-blur-md rounded-3xl p-6 md:p-10 text-white 
                shadow-[10px_-10px_20px_1px_rgba(0,_0,_0,_0.1)] 
                border border-white/10 ring-1 ring-white/5 transition-all duration-300 
                hover:scale-[1.02] hover:shadow-[12px_-12px_24px_2px_rgba(0,_0,_0,_0.15)] ease-in-out">

            {/* <div className="w-[85vw] md:w-[80vw] h-[80vh] flex flex-col justify-center 
            bg-gradient-to-br from-primaryGray/80 via-lightGray/10 to-primary/80 backdrop-blur-sm 
            rounded-3xl p-6 md:p-10 text-white shadow-[10px_-10px_20px_1px_rgba(0,_0,_0,_0.1)]
            "> */}
              <h3 className="text-3xl md:text-hero-xxl font-extrabold mb-4 md:mb-6">Visi</h3>
              <p className="text-base md:text-hero-m leading-relaxed max-w-xxl">
                Menjadi perusahaan terdepan dalam mendukung pengembangan ilmu pengetahuan, teknologi, dan sumber daya manusia di Indonesia.
              </p>
            </div>

            {/* MISI */}
            <div className="w-[85vw] md:w-[80vw] h-[80vh] flex flex-col justify-center 
  bg-gradient-to-br from-[#DE3433]/80 via-[#A9A9A9]/10 to-[#909194]/80 
  backdrop-blur-md rounded-3xl p-6 md:p-10 text-white 
  shadow-[10px_-10px_20px_1px_rgba(0,_0,_0,_0.1)] 
  border border-white/10 ring-1 ring-white/5 transition-all duration-300 
  hover:scale-[1.02] hover:shadow-[12px_-12px_24px_2px_rgba(0,_0,_0,_0.15)] ease-in-out">

              <h3 className="text-3xl md:text-hero-xxl font-extrabold mb-4 md:mb-12">Misi</h3>
              <ul className="list-disc pl-5 space-y-2 text-base md:text-hero-ss leading-relaxed max-w-xxl">
                <li>Memberikan layanan berkualitas tinggi yang berorientasi pada hasil</li>
                <li>Mendorong inovasi melalui kolaborasi lintas disiplin</li>
                <li>Meningkatkan kompetensi sumber daya manusia melalui pendidikan dan pelatihan</li>
                <li>Menyediakan solusi berbasis teknologi untuk mendukung kemajuan masyarakat</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Sticky Element Section (Setelah horizontal scroll) */}
        {/* <div ref={containerRef} className="relative h-screen w-full overflow-hidden bg-gradient-to-r from-pink-200 to-pink-100"> */}
        <div ref={containerRef} className="relative h-screen w-full overflow-hidden">
          <div className="sticky top-0 flex items-center justify-center h-screen max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-2 items-center justify-center 
                h-auto md:h-[700px] lg:h-[600px]  w-full
                px-4 sm:px-6 md:px-12 lg:px-20 
                pb-6 md:pb-0
                shadow-lg
                bg-secondaryGray
                mix-blend-soft-light 
                rounded-2xl
                hover:scale-[1.02] hover:shadow-[12px_-12px_24px_2px_rgba(0,_0,_0,_0.15)] transition-transform duration-300 ease-in-out
                ">
              {/* Left Side - Static Title */}
              {/* <div className="text-4xl sm:text-5xl md:text-hero-xxl font-black px-4 sm:px-6 py-4 text-center 
                bg-gradient-to-r from-darkRed via-text-secondary to-primaryGray bg-clip-text text-transparent"
              >
                Nilai kami
              </div> */}

              {/* <div className="relative inline-block text-primary text-[2.8rem] md:text-[4rem] leading-tight font-poppins font-extrabold min-h-[60px] bg-[length:200%_auto]"> */}
              <div className="relative inline-block font-poppins font-extrabold bg-[length:100%_auto]">
                <div className="relative z-10 text-4xl sm:text-5xl md:text-hero-xl font-black px-4 sm:px-6 py-4 text-center 
                  bg-gradient-to-r from-darkRed via-text-secondary to-primaryGray bg-clip-text text-transparent
                  ">
                  Our Value
                </div>
              {/* Shine Overlay */}
              {showShine && (
                <span className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
                  {/* Light mode shine */}
                  <span className="absolute inset-0 animate-shine-effect bg-gradient-to-r from-transparent via-secondaryGray to-transparent opacity-75 blur-sm dark:hidden" />

                  {/* Dark mode shine (pakai warna gelap transparan atau warna soft) */}
                  <span className="absolute inset-0 animate-shine-effect bg-gradient-to-r from-transparent via-secondaryGray to-transparent opacity-75 blur-sm hidden dark:block" />
                </span>
              )}
            </div>

              {/* Right Side - Scroll-triggered words */}
              <div className="relative h-[400px] sm:h-[400px] md:h-[700px] lg:h-[500px] w-full shadow-md rounded-lg bg-surface/10 overflow-hidden px-4 sm:px-6 py-4 text-base leading-relaxed">
                {wordsRight.map((word, i) => (
                  <div
                    key={i}
                    ref={(el) => {
                      if (el) scrollWordsRef.current[i] = el;
                    }}
                    className={`
                      absolute 
                      inset-2 sm:inset-3 
                      transition-opacity opacity-0 duration-500 ease-in-out 
                      flex items-center justify-center
                      rounded-lg shadow-lg 
                      p-2 sm:p-4 md:p-6 
                      transparent
                      text-white/80
                      text-[18px] sm:text-[26px] md:text-[20px] lg:text-[29px] font-bold
                      ${i === 0 ? 'opacity-100' : ''}
                    `}
                  >
                    {word}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <motion.div
          className="relative z-10 mt-16 flex justify-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <button
            onClick={handleNavigate}
            className="px-8 py-4 bg-surface text-white text-xl font-semibold rounded-full shadow-lg transition-all duration-500 hover:bg-backgroundDark/80 hover:scale-105"
          >
            Lihat Layanan Kami
          </button>
        </motion.div>
      </section>
  );
};

export default About;
